import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, orderBy, Timestamp, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore'; // Added serverTimestamp
import { db, updateAlatGelasStockOnReturn } from '../../firebase'; // Import stock update function
import './PersetujuanPermintaanAlat.css'; // Import the CSS file

// Helper function to group requests by submission ID
const groupRequestsBySubmission = (requests) => {
  const grouped = {};
  requests.forEach(req => {
    if (!req.submissionId) {
      // Handle legacy requests without submissionId (group by userId as before)
      if (!grouped[req.userId]) {
        grouped[req.userId] = {
          namaUser: req.namaUser,
          userNim: req.userNim,
          submissionId: null,
          tanggalPermintaan: req.tanggalPermintaan,
          requests: []
        };
      }
      grouped[req.userId].requests.push(req);
    } else {
      // Group by submissionId for new requests
      if (!grouped[req.submissionId]) {
        grouped[req.submissionId] = {
          namaUser: req.namaUser,
          userNim: req.userNim,
          submissionId: req.submissionId,
          tanggalPermintaan: req.tanggalPermintaan,
          requests: []
        };
      }
      grouped[req.submissionId].requests.push(req);
    }
  });
  
  // Sort groups by date (newest first)
  const sortedGrouped = {};
  Object.keys(grouped)
    .sort((a, b) => {
      const timeA = grouped[a].tanggalPermintaan?.seconds || 0;
      const timeB = grouped[b].tanggalPermintaan?.seconds || 0;
      return timeB - timeA;
    })
    .forEach(key => {
      sortedGrouped[key] = grouped[key];
    });
    
  return sortedGrouped;
};

// Helper to format Firestore Timestamp to readable date string
const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID'); // Indonesian locale
  }
  // Handle cases where it might already be a string or undefined
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

const PersetujuanPermintaanAlat = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingId, setUpdatingId] = useState(null); // Track which request is being updated
  const [expandedSubmission, setExpandedSubmission] = useState(null);

  // State for return dialog
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDetails, setReturnDetails] = useState(null);
  const [returnQuantities, setReturnQuantities] = useState({});
  const [returnRequests, setReturnRequests] = useState([]); // Array of requests from same student
  const [viewMode, setViewMode] = useState('today'); // 'today' or 'history'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Format: YYYY-MM-DD
  
  // Handle expanding/collapsing a submission group
  const handleSubmissionExpand = (submissionId) => {
    setExpandedSubmission(expandedSubmission === submissionId ? null : submissionId);
  };
  
  // Handle approving all requests in a submission
  const handleApproveAllInSubmission = async (submissionId, requests) => {
    if (!window.confirm(`Apakah Anda yakin ingin menyetujui semua ${requests.length} permintaan dalam pengajuan ini?`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Process all requests in the submission
      for (const request of requests) {
        if (request.status === 'pending') {
          await updateDoc(doc(db, 'permintaan_alat', request.id), { 
            status: 'approved',
            tanggalPersetujuan: serverTimestamp()
          });
        }
      }
      
      // Refresh the list
      fetchRequests();
      setSuccess(`Berhasil menyetujui semua permintaan dalam pengajuan.`);
    } catch (err) {
      console.error("Error approving all requests:", err);
      setError(`Gagal menyetujui semua permintaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle rejecting all requests in a submission
  const handleRejectAllInSubmission = async (submissionId, requests) => {
    if (!window.confirm(`Apakah Anda yakin ingin menolak semua ${requests.length} permintaan dalam pengajuan ini?`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Process all requests in the submission
      for (const request of requests) {
        if (request.status === 'pending') {
          await updateDoc(doc(db, 'permintaan_alat', request.id), { 
            status: 'rejected',
            tanggalPersetujuan: serverTimestamp()
          });
        }
      }
      
      // Refresh the list
      fetchRequests();
      setSuccess(`Berhasil menolak semua permintaan dalam pengajuan.`);
    } catch (err) {
      console.error("Error rejecting all requests:", err);
      setError(`Gagal menolak semua permintaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending, approved, and pending_return requests - optimized to fetch from selected date
  const fetchRequests = async (dateFilter = null) => { // Renamed function
    setLoading(true);
    setError('');
    try {
      let dateStart, dateEnd;
      
      if (viewMode === 'today' || !dateFilter) {
        // Get today's date range (start and end of day)
        const today = new Date();
        dateStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
        dateEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      } else if (viewMode === 'history' && dateFilter) {
        // Use selected date
        const selected = new Date(dateFilter);
        dateStart = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 0, 0, 0, 0);
        dateEnd = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 23, 59, 59, 999);
      } else {
        // No date filter - fetch all (for full history)
        dateStart = null;
        dateEnd = null;
      }

      const requestsRef = collection(db, 'permintaan_alat');
      let q;
      
      if (dateStart && dateEnd) {
        const dateStartTs = Timestamp.fromDate(dateStart);
        const dateEndTs = Timestamp.fromDate(dateEnd);
        q = query(
          requestsRef, 
          where('tanggalPermintaan', '>=', dateStartTs),
          where('tanggalPermintaan', '<=', dateEndTs)
        );
      } else {
        // Fetch all without date filter (for full history)
        q = query(requestsRef);
      }
      
      const querySnapshot = await getDocs(q);
      let data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      
      // Filter by status client-side to avoid composite index requirement
      // For history view, include all statuses including returned and cancelled
      const urgentStatuses = viewMode === 'today'
        ? ['pending', 'approved', 'admin_approved', 'pending_return']
        : ['pending', 'approved', 'admin_approved', 'pending_return', 'returned', 'cancelled'];
      data = data.filter(req => {
        if (!urgentStatuses.includes(req.status)) return false;
        
        // For 'approved' status, show all approved requests (they are still borrowing)
        // The return button will only appear for pending_return or approved with partial return
        // But the request itself should still be visible in the list
        return true;
      });

      // Sort requests: pending first, pending_return second, then by request date descending
      const statusOrder = { pending: 1, pending_return: 2, approved: 3, returned: 4, cancelled: 5, admin_approved: 6 };
      data.sort((a, b) => {
        const orderA = statusOrder[a.status] || 99;
        const orderB = statusOrder[b.status] || 99;
        if (orderA !== orderB) {
          return orderA - orderB; // Sort by status order first
        }
        // If status is the same, sort by request date descending (newest first)
        const timeA = a.tanggalPermintaan?.seconds || 0;
        const timeB = b.tanggalPermintaan?.seconds || 0;
        return timeB - timeA;
      });
      setRequests(data);
    } catch (err) {
      console.error("Error fetching tool requests:", err); // Updated error message
      setError('Gagal memuat data permintaan alat.');
    } finally {
      setLoading(false);
    }
  };

  // Handle status update (Approve/Reject)
  const handleUpdateRequestStatus = async (id, newStatus) => {
    setUpdatingId(id); // Set loading state for this specific request's buttons
    setError('');
    try {
      const requestRef = doc(db, 'permintaan_alat', id);
      await updateDoc(requestRef, { status: newStatus });
      console.log(`Request ${id} status updated to ${newStatus}.`);
      // Refetch requests to update the list instead of filtering
      fetchRequests();
    } catch (err) {
      console.error(`Error updating request ${id} status to ${newStatus}:`, err); // Added newStatus to error log
      setError(`Gagal memperbarui status permintaan ${id}.`);
    } finally {
      setUpdatingId(null); // Reset loading state
    }
  };

  // Handle deleting a request
  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus permintaan ini secara permanen?')) {
      return;
    }
    setUpdatingId(id); // Indicate loading/processing
    setError('');
    try {
      const requestRef = doc(db, 'permintaan_alat', id);
      await deleteDoc(requestRef);
      console.log(`Request ${id} deleted successfully.`);
      // Refetch requests to update the list
      fetchRequests();
    } catch (err) {
      console.error(`Error deleting request ${id}:`, err);
      setError(`Gagal menghapus permintaan ${id}.`);
    } finally {
      setUpdatingId(null); // Reset loading state
    }
  };

  // Initial fetch and refetch mechanism
  useEffect(() => {
    fetchRequests(); // Use the renamed function
  }, []);

  // Handle opening return dialog
  const handleOpenReturnDialog = async (request) => {
    console.log('=== Opening Return Dialog (Laboran) ===');
    console.log('Request clicked:', {
      id: request.id,
      userId: request.userId,
      namaUser: request.namaUser,
      jenisAlat: request.jenisAlat,
      status: request.status,
      submissionId: request.submissionId
    });
    
    // First, get all pending_return requests from the same student from current requests
    // Only include requests that actually need return (pending_return status OR approved with partial return)
    let sameStudentRequests = requests.filter(req => {
      if (req.userId !== request.userId || req.jenisAlat !== request.jenisAlat) return false;
      
      // Include if status is pending_return (mahasiswa sudah mengajukan pengembalian)
      if (req.status === 'pending_return') return true;
      
      // Include if status is approved and already has some returned amount (partial return case)
      if (req.status === 'approved') {
        const alreadyReturned = req.jumlahSudahDikembalikan || 0;
        if (alreadyReturned > 0) {
          const totalBorrowed = req.jumlah || 0;
          const remainingBorrowed = totalBorrowed - alreadyReturned;
          return remainingBorrowed > 0;
        }
      }
      
      return false;
    });
    
    console.log(`Found ${sameStudentRequests.length} pending_return requests in current view for ${request.namaUser || request.userId}`);
    console.log('Current view requests:', sameStudentRequests.map(r => ({
      id: r.id,
      namaAlat: r.namaAlat,
      submissionId: r.submissionId,
      tanggalPermintaan: r.tanggalPermintaan
    })));
    
    // Always fetch all pending_return requests for this student from database
    // to ensure we get all of them regardless of submission date, submissionId, or view mode
    try {
      const requestsRef = collection(db, 'permintaan_alat');
      // First, get all pending_return and approved requests for this user (only 2 where clauses to avoid index issues)
      // We need to query both statuses separately since Firestore doesn't support 'in' with multiple where clauses easily
      const userPendingReturnQuery = query(
        requestsRef,
        where('userId', '==', request.userId),
        where('status', '==', 'pending_return')
      );
      const userApprovedQuery = query(
        requestsRef,
        where('userId', '==', request.userId),
        where('status', '==', 'approved')
      );
      
      const [userPendingReturnSnapshot, userApprovedSnapshot] = await Promise.all([
        getDocs(userPendingReturnQuery),
        getDocs(userApprovedQuery)
      ]);
      
      console.log(`Database query returned ${userPendingReturnSnapshot.size} pending_return and ${userApprovedSnapshot.size} approved for user ${request.userId}`);
      
      // Combine and filter client-side by jenisAlat and check for remaining borrowed amount
      // Only include approved requests that already have some returned amount (partial return)
      const allPendingReturnRequests = [
        ...userPendingReturnSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })),
        ...userApprovedSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
      ].filter(req => {
        if (req.jenisAlat !== request.jenisAlat) return false;
        
        // For approved status, only include if already has some returned amount (partial return case)
        if (req.status === 'approved') {
          const alreadyReturned = req.jumlahSudahDikembalikan || 0;
          if (alreadyReturned > 0) {
            const totalBorrowed = req.jumlah || 0;
            const remainingBorrowed = totalBorrowed - alreadyReturned;
            return remainingBorrowed > 0;
          }
          return false; // Don't include approved requests without any return history
        }
        
        return true; // Include all pending_return requests
      });
      
      console.log(`After filtering by jenisAlat (${request.jenisAlat}): ${allPendingReturnRequests.length} requests`);
      console.log('Database requests:', allPendingReturnRequests.map(r => ({
        id: r.id,
        namaAlat: r.namaAlat,
        submissionId: r.submissionId,
        tanggalPermintaan: r.tanggalPermintaan,
        jenisAlat: r.jenisAlat
      })));
      
      // Merge with current requests, avoiding duplicates
      const existingIds = new Set(sameStudentRequests.map(req => req.id));
      const additionalRequests = allPendingReturnRequests.filter(req => !existingIds.has(req.id));
      console.log(`Found ${additionalRequests.length} additional requests not in current view`);
      sameStudentRequests = [...sameStudentRequests, ...additionalRequests];
      
      console.log(`Total pending_return requests after merge: ${sameStudentRequests.length}`);
      console.log('Final requests:', sameStudentRequests.map(r => ({
        id: r.id,
        namaAlat: r.namaAlat,
        submissionId: r.submissionId
      })));
    } catch (err) {
      console.error('Error fetching all pending_return requests:', err);
      // Continue with what we have from current requests
    }
    
    // Ensure we have at least the current request
    if (sameStudentRequests.length === 0) {
      console.warn('No requests found, using clicked request only');
      sameStudentRequests = [request];
    }
    
    setReturnRequests(sameStudentRequests);
    setReturnDetails(request); // Keep for backward compatibility
    
    // Initialize quantities for all requests
    // If partially returned, default to remaining amount, otherwise to total
    const initialQuantities = {};
    sameStudentRequests.forEach(req => {
      const totalBorrowed = req.jumlah || 0;
      const alreadyReturned = req.jumlahSudahDikembalikan || 0;
      const remainingBorrowed = totalBorrowed - alreadyReturned;
      // Default to remaining amount if partially returned, otherwise to total
      initialQuantities[req.id] = req.jumlahDikembalikan || (remainingBorrowed > 0 ? remainingBorrowed : totalBorrowed);
    });
    setReturnQuantities(initialQuantities);
    setReturnDialogOpen(true);
    console.log('=== Return Dialog Opened (Laboran) ===');
  };

  // Handle closing return dialog
  const handleCloseReturnDialog = () => {
    setReturnDialogOpen(false);
    setReturnDetails(null);
    setReturnRequests([]);
    setReturnQuantities({});
  };

  // Handle quantity change in return dialog
  const handleQuantityChange = (requestId, value) => {
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setReturnQuantities({ ...returnQuantities, [requestId]: numValue });
    }
  };

  // Handle approving return (single)
  const handleApproveReturn = async (request) => {
    setUpdatingId(request.id);
    setError('');
    try {
      // Get returned quantity from state - must be explicitly set by user
      let returnedQuantity = returnQuantities[request.id];
      const totalBorrowed = request.jumlah || 0;
      const alreadyReturned = request.jumlahSudahDikembalikan || 0;
      const remainingBorrowed = totalBorrowed - alreadyReturned;
      
      // Validate that returned quantity is set
      if (returnedQuantity === undefined || returnedQuantity === null || isNaN(returnedQuantity)) {
        throw new Error('Silakan masukkan jumlah yang akan dikembalikan');
      }
      
      const totalReturned = alreadyReturned + returnedQuantity;
      const finalRemainingBorrowed = totalBorrowed - totalReturned;
      
      // Validate returned quantity
      if (returnedQuantity <= 0) {
        throw new Error('Jumlah dikembalikan harus lebih dari 0');
      }
      if (returnedQuantity > remainingBorrowed) {
        throw new Error(`Jumlah dikembalikan (${returnedQuantity}) tidak boleh melebihi jumlah yang masih dipinjam (${remainingBorrowed})`);
      }
      
      // Update stock in alat_gelas collection
      if (request.jenisAlat === 'alat_gelas') {
      await updateAlatGelasStockOnReturn(
        request.namaAlat,
        returnedQuantity,
        request.alatId // Pass alatId if available for more reliable lookup
      );
      }

      // Determine status based on whether all items are returned
      const isFullyReturned = totalReturned >= totalBorrowed;
      const newStatus = isFullyReturned ? 'returned' : 'approved';

      // Update request status
      const updateData = {
        status: newStatus,
        jumlahSudahDikembalikan: totalReturned,
        jumlahMasihDipinjam: finalRemainingBorrowed,
        jumlahDikembalikanActual: returnedQuantity,
        tanggalDikembalikanActual: serverTimestamp(),
        catatanPengembalian: !isFullyReturned 
          ? `Pengembalian sebagian: ${returnedQuantity} dari ${totalBorrowed} (${alreadyReturned} sudah dikembalikan sebelumnya, ${finalRemainingBorrowed} masih dipinjam)`
          : returnedQuantity !== totalBorrowed
            ? `Jumlah dikembalikan berbeda dari jumlah dipinjam (${totalBorrowed})`
          : null
      };

      await updateDoc(doc(db, 'permintaan_alat', request.id), updateData);

      // Clean up active_instrument_usage only if fully returned (for instruments)
      if (request.jenisAlat === 'instrumen' && request.alatId && isFullyReturned) {
        try {
          const usageQuery = query(
            collection(db, 'active_instrument_usage'),
            where('alatId', '==', request.alatId),
            where('userId', '==', request.userId)
          );
          const usageSnapshot = await getDocs(usageQuery);
          if (!usageSnapshot.empty) {
            // Delete all active usage entries for this user and alat
            const batch = writeBatch(db);
            usageSnapshot.docs.forEach(usageDoc => {
              batch.delete(usageDoc.ref);
            });
            await batch.commit();
            console.log('Cleaned up active_instrument_usage for returned request');
          }
        } catch (cleanupError) {
          console.error('Error cleaning up active_instrument_usage:', cleanupError);
          // Don't throw - this is cleanup, not critical
        }
      }

      handleCloseReturnDialog();
      // Refresh with current view mode and date
      if (viewMode === 'today') {
      fetchRequests();
      } else {
        fetchRequests(selectedDate);
      }
      
      if (isFullyReturned) {
        setSuccess(`Berhasil menyetujui pengembalian lengkap untuk ${request.namaAlat}.`);
      } else {
        setSuccess(`Berhasil menyetujui pengembalian sebagian: ${returnedQuantity} dari ${totalBorrowed}. Masih ${finalRemainingBorrowed} yang dipinjam.`);
      }
    } catch (err) {
      console.error('Error approving return:', err);
      setError(`Gagal menyetujui pengembalian: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  // Handle approving all returns for the same student
  const handleApproveAllReturns = async () => {
    if (returnRequests.length === 0) return;
    
    setUpdatingId('all');
    setError('');
    try {
      const batch = writeBatch(db);
      
      for (const request of returnRequests) {
        // Get returned quantity from state, don't use fallback to request.jumlah
        // If not in state, calculate from remaining borrowed amount
        let returnedQuantity = returnQuantities[request.id];
        const totalBorrowed = request.jumlah || 0;
        const alreadyReturned = request.jumlahSudahDikembalikan || 0;
        const remainingBorrowed = totalBorrowed - alreadyReturned;
        
        // If returnedQuantity is not set or invalid, use remaining amount as default
        if (returnedQuantity === undefined || returnedQuantity === null || isNaN(returnedQuantity)) {
          returnedQuantity = remainingBorrowed > 0 ? remainingBorrowed : totalBorrowed;
        }
        
        const totalReturned = alreadyReturned + returnedQuantity;
        const finalRemainingBorrowed = totalBorrowed - totalReturned;
        
        // Validate returned quantity
        if (returnedQuantity <= 0) {
          console.warn(`Invalid return quantity for request ${request.id}: ${returnedQuantity}`);
          continue;
        }
        if (returnedQuantity > remainingBorrowed) {
          console.warn(`Return quantity (${returnedQuantity}) exceeds remaining (${remainingBorrowed}) for request ${request.id}`);
          continue;
        }
        
        // Update stock for alat gelas
        if (request.jenisAlat === 'alat_gelas') {
          await updateAlatGelasStockOnReturn(request.namaAlat, returnedQuantity, request.alatId);
        }

        // Determine status based on whether all items are returned
        const isFullyReturned = totalReturned >= totalBorrowed;
        const newStatus = isFullyReturned ? 'returned' : 'approved';

        const updateData = {
          status: newStatus,
          jumlahSudahDikembalikan: totalReturned,
          jumlahMasihDipinjam: finalRemainingBorrowed,
          jumlahDikembalikanActual: returnedQuantity,
          tanggalDikembalikanActual: serverTimestamp(),
          catatanPengembalian: !isFullyReturned 
            ? `Pengembalian sebagian: ${returnedQuantity} dari ${totalBorrowed} (${alreadyReturned} sudah dikembalikan sebelumnya, ${finalRemainingBorrowed} masih dipinjam)`
            : returnedQuantity !== totalBorrowed
              ? `Jumlah dikembalikan berbeda dari jumlah dipinjam (${totalBorrowed})`
              : null
        };

        const requestRef = doc(db, 'permintaan_alat', request.id);
        batch.update(requestRef, updateData);

        // Clean up active_instrument_usage only if fully returned (for instruments)
        if (request.jenisAlat === 'instrumen' && request.alatId && isFullyReturned) {
          try {
            const usageQuery = query(
              collection(db, 'active_instrument_usage'),
              where('alatId', '==', request.alatId),
              where('userId', '==', request.userId)
            );
            const usageSnapshot = await getDocs(usageQuery);
            if (!usageSnapshot.empty) {
              usageSnapshot.docs.forEach(usageDoc => {
                batch.delete(usageDoc.ref);
              });
            }
          } catch (cleanupError) {
            console.error('Error cleaning up active_instrument_usage:', cleanupError);
          }
        }
      }

      await batch.commit();
      
      handleCloseReturnDialog();
      // Refresh with current view mode and date
      if (viewMode === 'today') {
        fetchRequests();
      } else {
        fetchRequests(selectedDate);
      }
      setSuccess(`Berhasil menyetujui ${returnRequests.length} pengembalian alat.`);
    } catch (err) {
      console.error('Error approving all returns:', err);
      setError(`Gagal menyetujui pengembalian: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'today') {
      fetchRequests();
    } else {
      fetchRequests(selectedDate);
    }
  };

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    if (viewMode === 'history') {
      fetchRequests(newDate);
    }
  };

  return (
    <div className="persetujuan-alat-container">
      <h2>Persetujuan Permintaan Alat</h2>

      {/* View Mode Toggle and Date Picker */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => handleViewModeChange('today')}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ccc',
              backgroundColor: viewMode === 'today' ? '#1976d2' : '#fff',
              color: viewMode === 'today' ? '#fff' : '#000',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Hari Ini
          </button>
          <button
            onClick={() => handleViewModeChange('history')}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ccc',
              backgroundColor: viewMode === 'history' ? '#1976d2' : '#fff',
              color: viewMode === 'history' ? '#fff' : '#000',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Riwayat
          </button>
        </div>
        
        {viewMode === 'history' && (
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            style={{
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '1rem'
            }}
          />
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading && !requests.length ? ( // Show loading only on initial load
        <div className="loading-message">Memuat data permintaan...</div>
      ) : (
        <div className="persetujuan-alat-container">
          {Object.entries(groupRequestsBySubmission(requests)).map(([submissionId, submissionData]) => {
            // Check if this submission has pending_return requests
            const hasPendingReturn = submissionData.requests.some(req => {
              if (req.status === 'pending_return') return true;
              if (req.status === 'approved') {
                const alreadyReturned = req.jumlahSudahDikembalikan || 0;
                if (alreadyReturned > 0) {
                  const totalBorrowed = req.jumlah || 0;
                  const remainingBorrowed = totalBorrowed - alreadyReturned;
                  return remainingBorrowed > 0;
                }
              }
              return false;
            });
            
            // Find first pending_return request for return button
            const firstPendingReturnRequest = submissionData.requests.find(req => {
              if (req.status === 'pending_return') return true;
              if (req.status === 'approved') {
                const alreadyReturned = req.jumlahSudahDikembalikan || 0;
                if (alreadyReturned > 0) {
                  const totalBorrowed = req.jumlah || 0;
                  const remainingBorrowed = totalBorrowed - alreadyReturned;
                  return remainingBorrowed > 0;
                }
              }
              return false;
            });
            
            const pendingReturnCount = submissionData.requests.filter(req => {
              if (req.status === 'pending_return') return true;
              if (req.status === 'approved') {
                const alreadyReturned = req.jumlahSudahDikembalikan || 0;
                if (alreadyReturned > 0) {
                  const totalBorrowed = req.jumlah || 0;
                  const remainingBorrowed = totalBorrowed - alreadyReturned;
                  return remainingBorrowed > 0;
                }
              }
              return false;
            }).length;
            
            return (
            <div key={submissionId} className="submission-group">
              <div 
                className="submission-header"
                onClick={() => handleSubmissionExpand(submissionId)}
              >
                <div className="submission-info">
                  <h3>{submissionData.namaUser} ({submissionData.userNim || 'NIM tidak tersedia'})</h3>
                  <p>{submissionData.requests.length} permintaan - {formatDate(submissionData.tanggalPermintaan)}</p>
                </div>
                
                  <div className="batch-actions" onClick={(e) => e.stopPropagation()}>
                    {/* Batch approval buttons */}
                    {submissionData.submissionId && submissionData.requests.some(req => req.status === 'pending') && (
                      <>
                    <button
                      className="btn btn-approve"
                      onClick={() => handleApproveAllInSubmission(submissionId, submissionData.requests)}
                      disabled={loading}
                    >
                      Setujui Semua
                    </button>
                    <button
                      className="btn btn-reject"
                      onClick={() => handleRejectAllInSubmission(submissionId, submissionData.requests)}
                      disabled={loading}
                    >
                      Tolak Semua
                    </button>
                      </>
                    )}
                    
                    {/* Return button - show in same row as student name */}
                    {hasPendingReturn && firstPendingReturnRequest && (
                      <button
                        className="btn btn-approve"
                        style={{ backgroundColor: '#9c27b0', marginLeft: submissionData.requests.some(req => req.status === 'pending') ? '10px' : '0' }}
                        onClick={() => handleOpenReturnDialog(firstPendingReturnRequest)}
                        disabled={loading || updatingId === 'all'}
                      >
                        {updatingId === 'all' ? 'Memproses...' : `Periksa Pengembalian (${pendingReturnCount})`}
                      </button>
                    )}
                  </div>
                
                <span className="expand-icon">
                  {expandedSubmission === submissionId ? '▲' : '▼'}
                </span>
              </div>
              
              {expandedSubmission === submissionId && (
                <div className="persetujuan-alat-table-container">
                  <table className="persetujuan-alat-table">
                    <thead>
                      <tr>
                        <th>Nama Alat</th>
                        <th>Jumlah</th>
                        <th>Tgl Pinjam</th>
                        <th>Tgl Kembali</th>
                        <th>Tujuan</th>
                        <th>Dosen Terkait</th>
                        <th>Status</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissionData.requests.map((req) => (
                        <tr key={req.id}>
                          <td>{req.namaAlat || 'N/A'}</td>
                          <td>{req.jumlah || 'N/A'}</td>
                          <td>{formatDate(req.tanggalPinjam)}</td>
                          <td>{formatDate(req.tanggalKembali)}</td>
                          <td>{req.tujuanPenggunaan || 'N/A'}</td>
                          <td>{req.namaDosenTerkait || 'N/A'}</td>
                          <td>
                            <span className={`status-badge status-${req.status}`}>
                              {req.status === 'pending' ? 'Menunggu Persetujuan' :
                               req.status === 'approved' ? 'Disetujui' :
                               req.status === 'admin_approved' ? 'Disetujui Admin' :
                               req.status === 'cancelled' ? 'Dibatalkan' :
                               req.status === 'pending_return' ? 'Menunggu Pengembalian' :
                               req.status}
                            </span>
                          </td>
                          <td className="action-buttons">
                            {req.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                                  className="btn btn-approve"
                                  disabled={updatingId === req.id}
                                >
                                  {updatingId === req.id ? '...' : 'Setujui'}
                                </button>
                                <button
                                  onClick={() => handleUpdateRequestStatus(req.id, 'rejected')}
                                  className="btn btn-reject"
                                  disabled={updatingId === req.id}
                                >
                                  {updatingId === req.id ? '...' : 'Tolak'}
                                </button>
                              </>
                            )}
                            {(req.status === 'approved' || req.status === 'admin_approved') && (
                              <button
                                onClick={() => handleUpdateRequestStatus(req.id, 'cancelled')}
                                className="btn btn-cancel"
                                disabled={updatingId === req.id}
                              >
                                {updatingId === req.id ? '...' : 'Batalkan'}
                              </button>
                            )}
                            {req.status === 'cancelled' && (
                              <>
                                <button
                                  onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                                  className="btn btn-approve"
                                  style={{ marginRight: '5px' }}
                                  disabled={updatingId === req.id}
                                >
                                  {updatingId === req.id ? '...' : 'Setujui Ulang'}
                                </button>
                                <button
                                  onClick={() => handleDeleteRequest(req.id)}
                                  className="btn btn-delete"
                                  disabled={updatingId === req.id}
                                >
                                  {updatingId === req.id ? '...' : 'Hapus'}
                                </button>
                              </>
                            )}
                            {/* Return button removed - will be shown in batch row below */}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            );
          })}
          
          {/* Add batch return section for each student with pending_return (across all submissions) */}
          {(() => {
            // Group pending_return requests by userId + jenisAlat
            // Only include requests that actually need return (pending_return status OR approved with partial return)
            const pendingReturnByStudent = {};
            requests.forEach(req => {
              let shouldInclude = false;
              
              // Only include if status is pending_return (mahasiswa sudah mengajukan pengembalian)
              if (req.status === 'pending_return') {
                shouldInclude = true;
              } else if (req.status === 'approved') {
                // Only include if already has some returned amount (partial return case)
                // This means mahasiswa sudah pernah mengembalikan sebagian sebelumnya
                const alreadyReturned = req.jumlahSudahDikembalikan || 0;
                if (alreadyReturned > 0) {
                  const totalBorrowed = req.jumlah || 0;
                  const remainingBorrowed = totalBorrowed - alreadyReturned;
                  // Only show if still has remaining (not fully returned yet)
                  shouldInclude = remainingBorrowed > 0;
                }
              }
              
              if (shouldInclude) {
                const key = `${req.userId}_${req.jenisAlat}`;
                if (!pendingReturnByStudent[key]) {
                  pendingReturnByStudent[key] = {
                    userId: req.userId,
                    namaUser: req.namaUser,
                    jenisAlat: req.jenisAlat,
                    requests: []
                  };
                }
                pendingReturnByStudent[key].requests.push(req);
              }
            });
            
            // This section is now removed - return button is integrated into submission header above
            return null;
          })()}
          
          {requests.length === 0 && !loading && (
            <div className="empty-message">
              Tidak ada permintaan alat yang perlu diproses atau dibatalkan.
            </div>
          )}
        </div>
      )}

      {/* Return Approval Dialog */}
      {returnDialogOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
            <div className="modal-header">
              <h3>Persetujuan Pengembalian Alat</h3>
              <button className="close-button" onClick={handleCloseReturnDialog}>×</button>
            </div>
            <div className="modal-body">
              {returnRequests.length > 0 && (
                <>
                  <h4 style={{ marginBottom: '1rem' }}>
                    Mahasiswa: {returnRequests[0].namaUser} ({returnRequests[0].userNim || 'N/A'})
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Nama Alat</th>
                        <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>Dipinjam</th>
                        <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>Sudah Dikembalikan</th>
                        <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>Masih Dipinjam</th>
                        <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>Dikembalikan Sekarang</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnRequests.map((request) => {
                        const totalBorrowed = request.jumlah || 0;
                        const alreadyReturned = request.jumlahSudahDikembalikan || 0;
                        const remainingBorrowed = totalBorrowed - alreadyReturned;
                        const maxReturn = remainingBorrowed > 0 ? remainingBorrowed : totalBorrowed;
                        
                        return (
                          <tr key={request.id}>
                            <td style={{ padding: '8px', border: '1px solid #ddd' }}>{request.namaAlat}</td>
                            <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>{totalBorrowed}</td>
                            <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>
                              {alreadyReturned > 0 ? (
                                <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{alreadyReturned}</span>
                              ) : (
                                <span style={{ color: '#999' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>
                              {remainingBorrowed > 0 ? (
                                <span style={{ color: '#f57c00', fontWeight: 'bold' }}>{remainingBorrowed}</span>
                              ) : (
                                <span style={{ color: '#999' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>
                    <input
                      type="number"
                                value={returnQuantities[request.id] || ''}
                                onChange={(e) => handleQuantityChange(request.id, e.target.value)}
                      min="0"
                                max={maxReturn}
                                style={{ width: '80px', textAlign: 'right', padding: '4px' }}
                                title={alreadyReturned > 0 ? `Maks: ${maxReturn}` : ''}
                    />
                              {alreadyReturned > 0 && (
                                <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                  Maks: {maxReturn}
                  </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-cancel" onClick={handleCloseReturnDialog}>Batal</button>
              <button
                className="btn btn-approve"
                onClick={handleApproveAllReturns}
                disabled={updatingId === 'all' || returnRequests.length === 0}
              >
                {updatingId === 'all' ? 'Memproses...' : `Setujui Semua (${returnRequests.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersetujuanPermintaanAlat;
